import settings from "../config/settings";
import axios from "axios";

const ApiInstance = axios.create({
    baseURL: settings.api.url,
});

// Flag so refresh is not called twice
let isRefreshing = false;
let failedQueue = [];

// Helper to process queued requests after refreshing
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

const isPublicEndpoint = (url) => {
    return url && (
        url.includes('vendor/assessment/') ||
        url.includes('vendor/submit/') ||
        url.includes('vendor/upload-documents/') ||
        url.includes('vendor/question-suggestions/') ||
        url.includes('vendor/response/')
    );
};

// ------------------ REQUEST INTERCEPTOR ------------------
ApiInstance.interceptors.request.use(
    (req) => {
        const access_token = sessionStorage.getItem("access_token");

        req.headers = {
            ...req.headers,
            Authorization: settings.api.AUTH,
            "ngrok-skip-browser-warning": true,
        };

        // Skip token for public endpoints or if no token exists
        if (access_token && !isPublicEndpoint(req.url)) {
            req.headers["token"] = `Bearer ${access_token}`;
        }

        return req;
    },
    (error) => Promise.reject(error)
);

// ------------------ RESPONSE INTERCEPTOR ------------------
ApiInstance.interceptors.response.use(
    (response) => {
        const errorCode = response?.data?.error?.error_code;
        const errorMessage = response?.data?.error?.message;
        // console.log()

        if (errorCode === 403 && errorMessage === "Invalid or expired token: Token has expired" && !response.config._retry && !isPublicEndpoint(response.config.url)) {
            response.config._retry = true;
            return handleTokenRefresh(response.config);
        }

        return {
            statusCode: response?.data,
            errors: response?.data?.error,
        };
    },

    async (error) => {
        console.log(('error', error))
        const originalRequest = error.config;
        const errorCode = error?.response?.data?.error?.error_code;
        const errorMessage = error?.response?.data?.error?.message;
        const status = error?.response?.status;

        // Deployment in progress — gateway 5xx only. We deliberately do NOT
        // treat "no response" as a deploy, because that also catches the
        // user being offline (WiFi off, captive portal, DNS hiccup, CORS),
        // which would falsely log them out.
        if (status === 502 || status === 503 || status === 504) {
            window.dispatchEvent(new CustomEvent('maintenance_mode'));
        }

        // 406 = plan gate denied — signal the UI to show an upgrade modal
        if (status === 406) {
            window.dispatchEvent(new CustomEvent('plan_gate_denied', {
                detail: { message: errorMessage || 'Your current plan does not include access to this feature.' }
            }));
        }

        // 403 (RBAC denied, not token expiry) — signal the UI for a permission toast
        if (status === 403 && errorMessage !== "Invalid or expired token: Token has expired") {
            window.dispatchEvent(new CustomEvent('rbac_permission_denied', {
                detail: { message: errorMessage || 'You do not have permission to perform this action.' }
            }));
        }

        // Prevent refresh loop for public endpoints or if already retried
        if (errorCode === 403 && errorMessage === "Invalid or expired token: Token has expired" && !originalRequest._retry && !isPublicEndpoint(originalRequest.url)) {
            originalRequest._retry = true;
            return handleTokenRefresh(originalRequest);
        }

        return Promise.reject(error);
    }
);



// ------------------ REFRESH TOKEN FUNCTION ------------------
async function handleTokenRefresh(originalRequest) {
    try {
        const refresh_token = sessionStorage.getItem("refresh_token");

        if (!refresh_token) return Promise.reject("No refresh token");

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers["token"] = "Bearer " + token;
                return ApiInstance(originalRequest);
            });
        }

        isRefreshing = true;

        const response = await axios.post(
            `${settings.api.url}auth/refresh/`,
            { refresh_token },
            {
                headers: {
                    Authorization: settings.api.AUTH,
                    "Content-Type": "application/json",
                },
            }
        );

        const refreshData = response?.data?.response_data;
        const statusCode = refreshData?.status_code;

        const errorCode = response?.data?.error?.error_code;
        if (errorCode === 401) {
            sessionStorage.clear();
            window.location.href = "/";
            return;
        }
        // If refresh token expired → logout
        if (statusCode === 401) {
            sessionStorage.removeItem("access_token");
            sessionStorage.removeItem("refresh_token");
            processQueue("Refresh Token Expired", null);
            isRefreshing = false;
            window.location.href = "/";
            return Promise.reject("Refresh Expired");
        }

        // NEW TOKENS
        const newAccess = refreshData?.data?.access_token;
        const newRefresh = refreshData?.data?.refresh_token;

        // Save them
        sessionStorage.setItem("access_token", newAccess);
        sessionStorage.setItem("refresh_token", newRefresh);

        processQueue(null, newAccess);

        // Retry original request with new access token
        originalRequest.headers["token"] = `Bearer ${newAccess}`;

        isRefreshing = false;

        return ApiInstance(originalRequest);

    } catch (err) {
        console.log(" ApiInstance error", err)
        processQueue(err, null);
        isRefreshing = false;
        return Promise.reject(err);
    }
}

export default ApiInstance;
